import { handleWebParentMessage, pickAdapter } from "./adapters";
import { createBridge, type BridgeApi, type CallOptions } from "./bridge";
import { createEvents, type TypedEvents } from "./events";

export type { Adapter } from "./adapters";
export type { BridgeApi, BridgeInstance, CallOptions, Handler } from "./bridge";
export { BridgeError, type BridgeErrorShape, type Envelope } from "./envelope";
export {
  EVENTS,
  type BridgeEventName,
  type CloseReason,
  type ContextUpdate,
  type GetCredsResult,
  type HandshakeResult,
  type TypedEvents,
} from "./events";
export { createBridge } from "./bridge";
export { createEvents } from "./events";
export { createHostContext } from "./hostContext";
export type { ContextChannel, ContextEntry, HostContext, HostContextOptions } from "./hostContext";

export interface InitBridgeConfig {
  /**
   * Origins permitted to push events into the SPA. Empty/omitted accepts any origin —
   * the app relies on other mechanisms for host authentication.
   */
  allowedOrigins?: string[];
  /** Window to bind to. Defaults to the global `window`; injectable for tests. */
  window?: Window;
}

export interface InitializedBridge {
  callNativeBridge<T = unknown>(event: string, payload?: unknown, opts?: CallOptions): Promise<T>;
  bridge: BridgeApi;
  events: TypedEvents;
  receive(raw: unknown): void;
}

/**
 * Wire the bridge to the current host once at startup: detect the carrier, expose the
 * native→web receive global, and (for the web-parent carrier) install an origin-guarded
 * `message` listener. Returns the runtime surface feature code uses.
 */
export function initBridge(config: InitBridgeConfig = {}): InitializedBridge {
  const win = config.window ?? window;
  const adapter = pickAdapter(win);
  const instance = createBridge(adapter);
  const events = createEvents(instance);

  (
    win as unknown as { __tigerAgentBridge: { receive: (raw: unknown) => void } }
  ).__tigerAgentBridge = {
    receive: instance.receive,
  };

  const allowedOrigins = config.allowedOrigins ?? [];
  if (win.parent && win.parent !== win) {
    win.addEventListener("message", (ev) =>
      handleWebParentMessage(ev, {
        receive: instance.receive,
        allowedOrigins,
        parentSource: win.parent,
      }),
    );
  }

  return {
    callNativeBridge: instance.callNativeBridge,
    bridge: instance.bridge,
    events,
    receive: instance.receive,
  };
}
